import React, { useEffect, useState } from 'react';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useUser } from '@src/state/user/hooks';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useTermsOfUse } from '@src/state/TermsOfUse';
import messages from '@src/common/messages.json';
import { PageLayout } from '@src/common/PageLayout';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { config } from '@uhg-abyss/web/tools/config';
import { Link } from '@uhg-abyss/web/ui/Link';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { V2Text } from '@uhg-abyss/web/ui/Text';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { TinRegistration } from './TinRegistration';
import { UserBlockedModal } from './UserBlockedModel/UserBlockedModal';
import { TermsOfUseModel } from './TermsOfUse/TermsOfUseModel';
import { ViewSubmissions } from './ViewSubmissions';

const PageTitle = styled('p', {
  fontSize: '40px',
  fontWeight: 600,
  paddingTop: '20px',
  fontFamily: '$heading',
  color: '$primary1',
  maxWidth: '100%',
  lineHeight: '120%',
  margin: 'auto',
  display: 'block',
});

const PageSubtitle = styled('p', {
  fontWeight: 460,
  paddingTop: '10px',
  paddingBottom: '30px',
  fontSize: '16px',
  color: '$core.color.neutral.90',
  lineHeight: '120%',
  display: 'block',
});

const BulletList = styled('ul', {
  paddingLeft: '24px',
  margin: '0',
  listStyleType: 'disc',
});

const BulletItem = styled('li', {
  fontSize: '16px',
  fontWeight: 400,
  color: '$core.color.neutral.90',
  lineHeight: '120%',
  '&::marker': {
    color: '$core.color.neutral.90',
    fontSize: '11px',
  },
});

export const Landing = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const abyssForm = useFormV2();
  const { uuid } = useUser.state();
  const { toggleReturn } = useTinRegistration.actions();
  const { updateTerms, validateTerms } = useTermsOfUse.actions();

  const [errorRes, setErrorRes] = useState({
    error: {
      message: '',
    },
  });
  const [isAlertBanner, setIsAlertBanner] = useState(false);
  const [isTinRegError, setTinRegError] = useState(false);
  const [isTinRegStatusError, setTinRegStatusError] = useState(false);
  const { setDocumentId } = useTinRegistration.actions();
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [isTermsOfUse, setTermsOfUse] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    if (errorRes.error) {
      const { statusCode, faultCode } = errorRes.error;
      if (
        (statusCode === 500 && faultCode === 'NEASEE') ||
        statusCode === 409
      ) {
        setIsAlertBanner(true);
      } else if (faultCode === 'EPTSUB') {
        const ms = Date.parse(errorRes.error.timestamp) - Date.now();
        setTimeRemaining(ms);
        setIsUserBlocked(true);
      }
    }
  }, [errorRes]);

  const userInfo = useUser.state();
  useEffect(() => {
    const pageName = 'epvsecure:home';
    const billingTin = '';
    const siteSectionLevel = {
      siteSectionLevel1: 'home',
      siteSectionLevel2: '',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    adobeTaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });

    validateTerms(
      { uuid },
      (res) => {
        if (res && res.accepted) {
          setTermsOfUse(false);
        } else {
          setTermsOfUse(true);
        }
      },
      (error) => {}
    );

    // set billingTin value to empty string so that the value doesn't persist when user navigates back to landing page.
    abyssForm.setValue('searchRegistration.billingTin', '');
  }, []);

  const messageDescription =
    errorRes.error.faultCode === 'TSTCAL' ? (
      <V2Text css={{ marginBottom: '0px' }}>
        This TIN is already registered with UnitedHealthcare. If you would like
        to access the UnitedHealthcare Provider Portal,{' '}
        <Link
          id="provider-link"
          href={config('NEW_USER_REGISTRATION_URL')}
          target="_blank"
          rel="noopener noreferrer"
        >
          click here
        </Link>
        . (T-001)
      </V2Text>
    ) : (
      errorRes.error.message
    );

  const getMessage = () => {
    if (isTinRegError) {
      return messages.TIN_STATUS_FAILED;
    }
    if (isTinRegStatusError) {
      return messages.TIN_REGISTRATION_STATUS_FAILED;
    }
    return messageDescription;
  };

  const router = useRouter();
  const { setTrackingNumber } = useTinRegistration.actions();

  const populateLandingPage = (billingTin, requestId) => {
    abyssForm.setValue('searchRegistration.billingTin', billingTin);
    abyssForm.setValue('searchRegistration.requestId', requestId);
  };

  const populateW9 = (w9Details) => {
    if (w9Details.fileName && w9Details.doc360Id) {
      const restoredFile = new File(
        ['restored-file-placeholder'],
        w9Details.fileName,
        {
          type: 'application/pdf',
        }
      );
      setDocumentId({ id: 'w9form.fileName', value: w9Details.doc360Id });
      abyssForm.setValue('w9form.fileName', [restoredFile], {
        touched: false,
      });
    }

    abyssForm.setValue(
      'w9form.federalTaxClassification',
      w9Details.federalTaxClassification
    );
    abyssForm.setValue('w9form.businessName', w9Details.businessName);
    abyssForm.setValue('w9form.name', w9Details.name);
  };

  const populatePracticeData = (tinDetails, practiceInformation) => {
    let model = 'practiceInformation.registrationTypeSelection';
    abyssForm.setValue(model, practiceInformation.registrationType);
    const pType = practiceInformation.practiceType;
    abyssForm.setValue('practiceInformation.practiceTypeValue', pType);

    model = 'practiceInformation.practiceTypeSelection';
    abyssForm.setValue(model, pType);

    model = 'practiceInformation.restrictionSelection';
    if (practiceInformation.isRevoked) {
      abyssForm.setValue(model, 'Yes');
    } else if (tinDetails.address && tinDetails.address.length > 0) {
      abyssForm.setValue(model, 'No');
    }

    if (practiceInformation.additionalDetails) {
      model = 'practiceInformation.restrictionInput';
      abyssForm.setValue(model, practiceInformation.additionalDetails);
    }

    const npiList = Array.isArray(practiceInformation.npi)
      ? practiceInformation.npi
      : [];

    if (npiList.length > 1) {
      let inputStr = '';
      npiList.forEach((val) => {
        inputStr = `${inputStr + val},`;
      });
      inputStr = inputStr.substring(0, inputStr.length - 1);
      model = 'practiceInformation.npiInformation';
      abyssForm.setValue(model, inputStr);
    } else {
      model = 'practiceInformation.npiInformation';
      abyssForm.setValue(model, npiList[0] || '');
    }

    model = 'practiceInformation.cliaFile';
    if (practiceInformation.fileName && practiceInformation.doc360IdCms) {
      const restoredFile = new File(
        ['restored-file-placeholder'],
        practiceInformation.fileName,
        {
          type: 'application/pdf',
        }
      );
      abyssForm.setValue(model, [restoredFile], {
        touched: false,
      });
      setDocumentId({ id: model, value: practiceInformation.doc360IdCms });
    }

    model = 'practiceInformation.cliaIdentification';
    abyssForm.setValue(model, practiceInformation.cliaId);

    model = 'practiceInformation.medicareId';
    abyssForm.setValue(model, practiceInformation.medicareId);

    model = 'practiceInformation.isNpiValid';
    abyssForm.setValue(model, practiceInformation.isNpiValid);

    model = 'practiceInformation.medLicense';
    abyssForm.setValue(model, practiceInformation.licenses);

    if (practiceInformation.licenses) {
      practiceInformation.licenses.forEach((val, index) => {
        model = `practiceInformation.medLicense[${index}].licenseType`;
        abyssForm.setValue(model, val.licenseType);
        model = `practiceInformation.medLicense[${index}].licenseNumber`;
        abyssForm.setValue(model, val.licenseNumber);
        model = `practiceInformation.medLicense[${index}].state`;
        abyssForm.setValue(model, val.state);
      });
    }
  };

  const populateLocation = (locationInfo) => {
    let model = '';
    let type = '';
    let doc360Ext = 'Proof';
    const regex = new RegExp('(?:SecondaryService).');

    locationInfo.forEach((val) => {
      if (val.type === 'PrimaryService') {
        type = 'primaryServicing';
      }
      if (val.type === 'PrimaryBilling') {
        type = 'primaryBilling';
      }
      if (val.type === 'PrimaryRemit') {
        type = 'primaryRemittance';
      }
      if (regex.test(val.type)) {
        const index = val.type.substring(16, 17) - 1;
        type = `secondaryServicing[${index}]`;
        doc360Ext = '.proof';
      }
      model = `address.${type}.streetLine1`;

      abyssForm.setValue(model, val.streetLine1);
      model = `address.${type}.streetLine2`;
      abyssForm.setValue(model, val.streetLine2);
      model = `address.${type}.city`;
      abyssForm.setValue(model, val.city);
      model = `address.${type}.state`;
      abyssForm.setValue(model, val.state);
      model = `address.${type}.zipCode`;
      abyssForm.setValue(model, val.zipCode);

      if (val.doc360Id && val.fileName) {
        const restoredFile = new File(
          ['restored-file-placeholder'],
          val.fileName,
          {
            type: 'application/pdf',
          }
        );
        model = `address.${type}${doc360Ext}`;
        abyssForm.setValue(model, [restoredFile], { touched: false });
        setDocumentId({ id: model, value: val.doc360Id });
      }
    });
  };

  const populateAttestation = (attestFields) => {
    const submitterName = (attestFields.name || '').trim();
    const [firstName, ...lastNameParts] = submitterName
      .split(/\s+/)
      .filter(Boolean);
    const lastName = lastNameParts.join(' ');

    // Verify TIN page uses V2 models under submitterInformation.
    let model = 'submitterInformation.firstName';
    abyssForm.setValue(model, firstName || '');
    model = 'submitterInformation.lastName';
    abyssForm.setValue(model, lastName || '');
    model = 'submitterInformation.jobTitle';
    abyssForm.setValue(model, attestFields.jobTitle || '');
    model = 'submitterInformation.emailAddress';
    abyssForm.setValue(model, attestFields.email || '');
    model = 'submitterInformation.phoneNumber';
    abyssForm.setValue(model, attestFields.phoneNumber || '');
    model = 'submitterInformation.preferredModeOfContact';
    abyssForm.setValue(model, attestFields.communicationPreference || '');
    model = 'submitterInformation.submittedClaim';
    abyssForm.setValue(model, attestFields.isClaimSubmitted ? 'Yes' : 'No');
    model = 'submitterInformation.claimDate';
    abyssForm.setValue(
      model,
      attestFields.isClaimSubmitted ? attestFields.claimDate || '' : ''
    );
  };

  const onYes = (tinDetails) => {
    setTrackingNumber(tinDetails.trackingNumber);
    if (tinDetails && tinDetails.billingTin) {
      populateLandingPage(tinDetails.billingTin, tinDetails.requestId);
    }
    if (tinDetails && tinDetails.w9Details) {
      populateW9(tinDetails.w9Details);
    }
    if (tinDetails && tinDetails.practiceInformation) {
      populatePracticeData(tinDetails, tinDetails.practiceInformation);
    }
    if (tinDetails && tinDetails.address && tinDetails.address.length > 0) {
      populateLocation(tinDetails.address);
    }
    if (tinDetails && tinDetails.attestationDetails) {
      populateAttestation(tinDetails.attestationDetails);
    }

    toggleReturn(true);

    const eventInteraction = 'jobStartSavedTinRegistration';
    const jobName = 'tin registration';
    const registrationContext = 'already saved registration';
    const providerBillingTaxID = tinDetails.billingTin;
    adobeTaggingEvent('SAVED_REGISTRATION', {
      eventInteraction,
      jobName,
      registrationContext,
      providerBillingTaxID,
    });

    abyssForm.validate('searchRegistration', (values) => {
      router.navigate('/verify-tin');
    });
  };

  const populateDetails = (tinDrafts) => {
    setTrackingNumber(tinDrafts.trackingNumber);
    if (tinDrafts && tinDrafts.billingTin) {
      populateLandingPage(tinDrafts.billingTin, tinDrafts.requestId);
    }
    if (tinDrafts && tinDrafts.w9Details) {
      populateW9(tinDrafts.w9Details);
    }
    if (tinDrafts && tinDrafts.practiceInformation) {
      populatePracticeData(tinDrafts, tinDrafts.practiceInformation);
    }
    if (tinDrafts && tinDrafts.address && tinDrafts.address.length > 0) {
      populateLocation(tinDrafts.address);
    }
    if (tinDrafts && tinDrafts.attestationDetails) {
      populateAttestation(tinDrafts.attestationDetails);
    }

    toggleReturn(true);

    const eventInteraction = 'jobStartSavedTinRegistration';
    const jobName = 'tin registration';
    const registrationContext = 'already saved registration';
    const providerBillingTaxID = tinDrafts.billingTin;
    adobeTaggingEvent('SAVED_REGISTRATION', {
      eventInteraction,
      jobName,
      registrationContext,
      providerBillingTaxID,
    });

    abyssForm.validate('searchRegistration', (value) => {
      router.navigate('/verify-tin');
    });
  };
  const closeUserBlockedModel = () => {
    setIsUserBlocked(false);
  };

  const closeTermsOfUseModel = () => {
    updateTerms(
      {
        uuid,
        accepted: true,
        domain: 'EPV-Portal',
      },
      (response) => {
        setTermsOfUse(false);
      },
      (error) => {
        setTermsOfUse(true);
      }
    );
  };

  const onLogout = () => {
    const useUpdatedAuth = featureToggles.getRow(
      'US10732847_UPDATED_CORE_V2_ENDPOINTS'
    )?.active;

    const logoutUrl = useUpdatedAuth
      ? `${config('IDENTITY_API_URL')}/logout/v2`
      : `${config('IDENTITY_API_URL')}/logout`;
    window.location = logoutUrl;
  };

  const handleLogout = async () => {
    onLogout();
  };

  const events = {
    onSignout: () => {
      if (config('APP_ENV') !== 'local') {
        handleLogout();
      }
    },
  };

  return (
    <PageLayout>
      <PageTitle id="dashboard-name">
        Verify your tax identification number (TIN)
      </PageTitle>
      <PageSubtitle>
        Start here to verify your tax identification number (TIN) with
        UnitedHealthcare. Verifying your TIN will not initiate a contract with
        UnitedHealthcare.
      </PageSubtitle>

      {(isAlertBanner || isTinRegError || isTinRegStatusError) && (
        <V2Alert
          status="error"
          title={
            isTinRegStatusError
              ? 'We were unable to retrieve your TIN registration'
              : 'Submission Error'
          }
          dismissible={false}
          inline
          css={{ 'abyss-alert-root': { marginBottom: '15px' } }}
        >
          {getMessage()}
        </V2Alert>
      )}

      {isTermsOfUse && (
        <TermsOfUseModel
          isOpen={isTermsOfUse}
          closeModal={closeTermsOfUseModel}
        />
      )}

      {isUserBlocked && (
        <UserBlockedModal
          isOpen={isUserBlocked}
          closeModal={closeUserBlockedModel}
          events={events}
          timeRemaining={timeRemaining}
        />
      )}

      <Grid>
        <Grid.Col span={{ xs: '100%', md: '50%' }}>
          <TinRegistration
            setErrorRes={setErrorRes}
            errorRes={errorRes}
            setIsAlertBanner={setIsAlertBanner}
            setTinRegError={setTinRegError}
            setTinRegStatusError={setTinRegStatusError}
            onYes={onYes}
            populateDetails={populateDetails}
            setIsUserBlocked={setIsUserBlocked}
            setTimeRemaining={setTimeRemaining}
          />
        </Grid.Col>

        <Grid.Col span={{ xs: '100%', md: '50%' }}>
          <V2Card
            css={{
              marginBottom: '24px',
              padding: '24px 20px',
            }}
          >
            <Layout.Group css={{ marginBottom: '7px' }}>
              <IconBrand icon="clipboard" size="33px" />

              <V2Text
                fontWeight="bold"
                css={{ fontSize: '22px', color: '$primary1' }}
              >
                What you&apos;ll need to get started
              </V2Text>
            </Layout.Group>

            <BulletList>
              <BulletItem>Your organization&apos;s TIN</BulletItem>
              <BulletItem>
                W-9 tax form completed within the last year (PDF version)
              </BulletItem>
              <BulletItem>
                Documentation for proof of primary servicing address, primary
                billing address and primary remittance addresses
              </BulletItem>
            </BulletList>
          </V2Card>

          <V2Alert status="info" dismissible={false} inline>
            Out-of-network facilities: Verify your TIN by submitting paper
            claims with a completed W-9 attached.
          </V2Alert>
        </Grid.Col>
      </Grid>

      <ViewSubmissions onYes={onYes} />
    </PageLayout>
  );
};
